/**
 * CrmLeadSources - Tela de gestao de Origens de Lead do CRM
 *
 * Lista todas as origens (sistema + custom) e permite ao admin do tenant
 * criar, editar, ativar/desativar e excluir apenas as origens CUSTOM.
 * Origens de sistema aparecem como read-only com badge "Sistema".
 *
 * RELEASE: 2026-07-10 v1.0.0 - Versão inicial.
 * RELEASE: 2026-07-10 v1.1.0 - Padronização visual: shell alinhado ao padrão
 *          de `TaskPhases` (header compacto, card com tabela, empty state em
 *          card) e badge de status unificado no padrão de Departamentos
 *          (Badge outline + StatusDot).
 */
import React, { useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Lock, Megaphone, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useLeadSources,
  useDeleteLeadSource,
} from '@/hooks/useLeadSources';
import { getLeadSourceIcon, type LeadSourceEntry } from '@/lib/lead-sources';
import { LeadSourceFormDialog } from '@/components/crm/LeadSourceFormDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { createLogger } from '@/lib/logger';

const logger = createLogger('CrmLeadSources');

export default function CrmLeadSources() {
  const { sources, isLoading, refetch } = useLeadSources(true);
  const del = useDeleteLeadSource();
  const { canCreate: pCanCreate, canEdit: pCanEdit, canDelete: pCanDelete } = usePermissions();

  // Permissoes granulares por menuKey
  const canCreate = pCanCreate('crm_param_lead_sources');
  const canEdit = pCanEdit('crm_param_lead_sources');
  const canDelete = pCanDelete('crm_param_lead_sources');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LeadSourceEntry | null>(null);
  const [pendingDelete, setPendingDelete] = useState<LeadSourceEntry | null>(null);
  const [search, setSearch] = useState('');

  // Filtro simples por nome/identificador
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sources;
    return sources.filter(
      (s) =>
        s.label.toLowerCase().includes(term) ||
        s.id.toLowerCase().includes(term),
    );
  }, [sources, search]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (s: LeadSourceEntry) => {
    setEditing(s);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await del.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
      refetch();
    } catch (e) {
      logger.error('Erro ao excluir origem', e);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header compacto — padrão TaskPhases */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Origens de Lead</h1>
        </div>
        {canCreate && (
          <Button
            size="xs"
            onClick={openCreate}
            className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <Plus className="mr-1.5" />
            Nova Origem
          </Button>
        )}
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar origens..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Tabela / Empty state */}
        {!isLoading && filtered.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Megaphone className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhuma origem encontrada</p>
              <p className="text-sm mt-1">
                {search
                  ? 'Tente ajustar sua busca'
                  : 'Crie a primeira origem personalizada para o seu CRM'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Ícone</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Identificador</TableHead>
                  <TableHead className="w-[110px]">Tipo</TableHead>
                  <TableHead className="w-[110px]">Status</TableHead>
                  <TableHead className="w-[90px] text-center">Ordem</TableHead>
                  <TableHead className="w-[120px] text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Carregando origens...
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading &&
                  filtered.map((s) => {
                    const Icon = getLeadSourceIcon(s.id) ?? Megaphone;
                    return (
                      <TableRow key={s.id}>
                        <TableCell>
                          <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{s.label}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{s.id}</code>
                        </TableCell>
                        <TableCell>
                          {s.is_system ? (
                            <Badge variant="secondary" className="gap-1">
                              <Lock className="h-3 w-3" />
                              Sistema
                            </Badge>
                          ) : (
                            <Badge variant="outline">Personalizada</Badge>
                          )}
                        </TableCell>
                        {/* Status — padrão unificado (Badge outline + StatusDot) */}
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                          >
                            <StatusDot tone={s.is_active ? 'emerald' : 'muted'} />
                            {s.is_active ? 'Ativa' : 'Inativa'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground text-sm">
                          {s.sort_order}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {!s.is_system && canEdit && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEdit(s)}
                                title="Editar"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {!s.is_system && canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => setPendingDelete(s)}
                                title="Excluir"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <LeadSourceFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
      />

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir origem?</AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a excluir a origem <strong>{pendingDelete?.label}</strong>.
              Se houver negócios utilizando esta origem, ela será apenas desativada
              (não aparecerá em novos negócios, mas continuará visível nos existentes).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
